/**
 * **The built-in reaction registry** — the **prompting, pre-commit** sibling of the post-commit
 * observer registries (`MOVE_LISTENER_RECOMPUTERS`/`SETTLE_RECOMPUTERS`, and the rule lists `TURN_ENDED_RULES` / `ATTACK_RESOLVED_RULES`).
 * Where those fire-and-forget *after* an event commits, this fires the *interception window*:
 * after the triggering attack's damage is **rolled** but **before `hp_delta` commits**, the
 * resolver consults this fixed, deterministically-ordered, append-only list of *eligible
 * target-side reactions*. Its one built-in member offers **Brace** to a mech target that still
 * has its reaction.
 *
 * **Built-in, NOT the content `fireTrigger` bus.** Brace is a mech *default* reaction (every mech
 * has it, core rules), so it is an engine built-in — exactly like the observer registries — never
 * an authored `TriggeredEffect`/`on_damage_taken` handler. The Long Rim content cases (Aegis,
 * Intercede, Argonaut) are authored triggers riding this same window.
 *
 * **The two Brace clauses split by where they land** (RAW Core p. 73, verified against the
 * rulebook): the *Resistance to the triggering attack* clause is injected into that attack's A2
 * `mitigate` path (the `resistant` flag this returns — Resistance already lives there, halving
 * after Armor, rounding up per Golden Rule II); the *+1 Difficulty to all other attacks until end
 * of the bracer's next turn* clause is the durational `Braced` status_add this emits (a
 * target-side `targetAccuracy: -1` modifier, `target_next_turn_end` expiry). No bespoke
 * `reduce_pending_damage` atom — that is reserved for the Long Rim cases.
 *
 * **Iterate-pure, like every prompt.** When the offer is eligible, interactive, and unanswered,
 * {@link gatherReaction} throws a {@link NeedsDecisionSignal} carrying `who = target` (the
 * cross-actor field); the resolver catches it and returns `needs_decision`. A recorded answer in
 * `intent.choices` (`brace`/`decline`) is honored deterministically — so replay reproduces the
 * defender's choice. Non-interactive with no recorded answer defaults to **decline** (nothing
 * happens), the safe deterministic default for replay / the CLI.
 */

import { asReactionLink } from "./reactionLink";
import {
  type EncounterState,
  type Combatant,
  type DamageType,
  weaponInstanceKey,
  type ResolvedStep,
  type StatusAddStep,
  reactionSlotOpen,
  resourceCapRawKey,
} from "@lancer-vtt/domain";
import { affordable, reactionLedger, settleEvents } from "./events/gameEvents";
import type { RequestAttack } from "@lancer-vtt/shared";
import {
  NeedsDecisionSignal,
  fireTrigger,
  gatherHandlersAt,
  promptingChoices,
  recordedChoices,
  staticHandlers,
  gatherStatusHandlers,
  gatherPilotHandlers,
  combatantEffectIds,
  interpretEffect,
  evaluateMatch,
  evaluatePredicate,
  resolveActorId,
  SEED_OVERLAY,
  type LoadedOverlay,
  type EffectContext,
  type PredicateContext,
  type DamageEvent,
  type Handler,
  type EffectAtom,
  type TriggerFamily,
  type ForcedAttackRequest,
} from "./effects";
import { clearHiddenSteps } from "./hide";
import { eligiblePickWeapons, findWeaponPick } from "./forcedAttackPick";
import { combatantRange, combatantLineOfSight } from "./effects/spatialAnswerer";
import { BRACED_STATUS, checkReactionStatusLegality } from "./statusLegality";
import { rollDamage, type DiceSource } from "../dice";
import type { InterceptSink, NonAttackReactionSpend } from "./nonAttackIntercept";

/**
 * The status name the Brace reaction lands (the `Braced` overlay entry's id — a target-side
 * `targetAccuracy: -1` modifier, `seedEntries.ts`). The +1-Difficulty-to-incoming-attacks clause,
 * and the no-reactions lockout `checkReactionStatusLegality` reads. Defined in the
 * `statusLegality.ts` leaf (which needs it) and re-exported here.
 */
export { BRACED_STATUS };

/** The recorded-answer option ids a reaction offer presents. */
export const BRACE_OPTION = "brace";
export const DECLINE_OPTION = "decline";

/** The facts a reaction offer reads to decide whether (and how) it fires. */
export type ReactionContext = {
  state: EncounterState;
  /** The attacker whose attack is triggering the interception window. */
  attacker: Combatant;
  /** The target the attack hit — the side a defensive reaction protects. */
  target: Combatant;
  /** Recorded answers (`intent.choices`) — a `brace`/`decline` answer records under the prompt id. */
  choices: Record<string, string> | undefined;
  /** Interactive mode: an eligible, unanswered offer throws to round-trip a prompt (else defaults). */
  interactive: boolean;
  /**
   * **Is the triggering attack ranged?** (`npc-features-fielded-roster` Phase 7.2.) RAW's Rebound
   * trigger ("you take damage from a ranged attack") is the motivating case: a tech attack is
   * also NOT ranged (the same complement-encoding fact {@link import("./effects/family").AttackMatch.ranged}
   * already carries for the attack family). The caller (`applyHitDamage`, `resolve/damageApply.ts`)
   * already computes this ONCE per attack resolution (`rangedAttack`, `resolve/attackFacts.ts`) —
   * threaded here rather than re-derived. Folded into the `PredicateContext.event` every gather in
   * this file builds (a {@link import("./effects/predicate").DamageEvent}), so an authored
   * `match: { family: "damage", ranged: true }` clause can gate on it.
   */
  ranged: boolean;
  /**
   * **Is the triggering attack a tech attack?** Threaded beside {@link ranged} from the same
   * `attackFacts.ts` derivation, so a melee attack reads `{ ranged: false, tech: false }` on the
   * {@link import("./effects/predicate").DamageEvent} (Blademaster's Parry). Absent reads as unset.
   */
  tech?: boolean;
  /**
   * **Did the triggering attack hit?** `true` from the hit arm, `false` from the Reliable-floor miss
   * (`gatherReliableMissReaction`); folded onto the `DamageEvent` like `ranged`, so a
   * `match: { family: "damage", hit: true }` clause (Return Fire's "hits you") stays off a miss.
   * Absent reads as unset on the event: no `hit` match holds.
   */
  hit?: boolean;
  /**
   * **The attack's damage types** (`npc-gaps` 2.6): every type its rolled instances carry, folded
   * onto the {@link import("./effects/predicate").DamageEvent} like {@link ranged}. The settled pass
   * replaces it with the types that survived mitigation. Absent: no `dtype` match holds.
   */
  dtypes?: readonly DamageType[];
  /**
   * The authority's dice — threaded so a compiled authored `on_damage_taken` offer's `outcome` can
   * interpret atoms that draw dice (a `reduce_pending_damage`-style roll). Unused by Brace itself
   * (a pure status/flag grant).
   */
  dice: DiceSource;
  /**
   * The loaded overlay — {@link gatherReaction} folds the target's active-status
   * `on_damage_taken` `optional: true` clauses off this and appends them to
   * {@link REACTION_OFFERS} (built-ins first, authored after — order preserved).
   * Omitted → {@link SEED_OVERLAY} (the production default, mirroring every other seed
   * read-site's `overlay = SEED_OVERLAY` convention).
   */
  overlay?: LoadedOverlay;
  /**
   * **Combatants that already spent a reaction earlier in THIS resolution** — the area fan-out's
   * in-flight ledger (`area-attack-reaction-offers` Phase 2.3, D5). Threaded straight into {@link
   * reactionAvailable}, whose doc carries the full reasoning. Absent on the single-target spine.
   */
  spentReactions?: ReadonlySet<string>;
  /**
   * **The attack that opened this window** (`npc-features-fielded-roster` Phase 11.1). Threaded
   * into an authored offer's `EffectContext.triggeringAttack`, so `forced_attack`'s repeat form
   * (Rebound's reflection) can re-resolve it.
   */
  triggeringAttack?: RequestAttack;
  /**
   * **No reaction window opens on this attack** (a `forced_attack` with `reactions: false` —
   * Rebound's reflection). {@link gatherReaction} consults no offer; automatic clauses still run.
   */
  offersClosed?: boolean;
};

/**
 * What consulting the reaction registry produced — folded back into the triggering attack by the
 * resolver. `resistant` feeds the A2 `mitigate` path (Brace ⇒ Resistance); `steps` are spliced
 * into the attack's recorded step list (the `Braced` status_add); `spendReactionBy` names the
 * defender whose reaction the room must spend as a **sibling `spend_reaction` in the attacker's
 * batch** (so undoing the attack refunds the Brace). The decline / no-offer case is inert.
 *
 * **Pending-damage transforms** — composed by {@link gatherReaction} across every eligible
 * offer/automatic effect on the one hit, then applied by the hit-damage stage ({@link
 * import("./resolve/damageApply").applyHitDamage}, which resolves the protector link once and
 * runs the shares per instance) AFTER `resistant` folds into `mitigate`:
 *   - `reduce` — Inspiring Presence: a recorded dice draw subtracted from the target's own
 *     POST-mitigation damage, floored at 0. Summed across every offer that contributes one (only
 *     one is authored today, but composition stays additive).
 *   - `split` — Argonaut Shield: the bound partner combatant shares the hit's PRE-mitigation
 *     damage (`ceil(pre/2)` to the partner, the remainder to the target — the double-count trap),
 *     each side then mitigating its own share with its own defenses. At most one split is expected
 *     per hit (a target bound to two simultaneous protectors is out of scope); the last one
 *     gathered wins if that ever happens.
 *   - `redirect` — Intercede: the FULL pre-mitigation damage moves to the protector (a distinct
 *     combatant from the target — the third routing case), who mitigates it with THEIR OWN
 *     defenses; the target takes NOTHING (never a second independent halving, never `split`'s
 *     `ceil(pre/2)`). Gathered by the protector-gather direction (`compileProtectorOffers`), NOT
 *     the target's own overlay. At most one redirect is expected per hit; if a redirect AND a split
 *     are both present (no shipped content does this), redirect wins (the last one gathered) —
 *     `resolve/damageApply.ts` treats them as mutually exclusive protector links.
 */
export type ReactionOutcome = {
  /** True ⇒ the triggering attack's damage is mitigated with Resistance (halve after Armor). */
  resistant: boolean;
  /** Grounded steps to splice into the attack resolution (the durational `Braced` status_add). */
  steps: ResolvedStep[];
  /**
   * The automatic clauses that emitted {@link steps} (`Probe`, Spikes), for the steps' `reaction`
   * link when no reaction was spent to name it (`chained-resolution-sequence` 4.3).
   */
  named?: string[];
  /**
   * Every reactor whose reaction the room must spend as a batch sibling this hit — a plain array,
   * not a single winner-take-last slot, because one hit can have BOTH the target bracing AND a
   * protector interceding: two DIFFERENT combatants each spending their own named reaction. Each
   * entry's `name` is the reaction's own named-cap key
   * (`activation.reactionUses[combatantId][name]`) — `"brace"` for Brace, `"intercede"` for
   * Intercede. Empty/`undefined` ⇒ nothing spent this hit.
   */
  spendReactionBy?: { combatantId: string; name: string }[];
  /** Reduce the target's own post-mitigation damage by this much (floored at 0 at the apply site). */
  reduce?: number;
  /** Split the hit's pre-mitigation damage with a bound partner (`ceil(pre/2)` to the partner). */
  split?: { partnerId: string };
  /** Redirect the hit's FULL pre-mitigation damage to a bound protector (the target takes nothing). */
  redirect?: { partnerId: string };
  /**
   * **Advisory warnings an authored offer's own `effect` raised** (`npc-features-fielded-roster`
   * Phase 7.4 — Rebound's own `advisory` atom, naming its un-automated reflection). Threaded from
   * {@link import("./effects/interpret").InterpretResult.warnings} by {@link compileAuthoredOffers}/
   * {@link compileProtectorOffers}'s own `interpretEffect` call — NOT by the built-in offers
   * (`REACTION_OFFERS`), which raise none. Empty/`undefined` ⇒ nothing to surface. {@link
   * gatherReaction} concatenates every eligible offer's warnings (mirroring `steps`'s own
   * composition), and {@link import("./resolve/damageApply").applyHitDamage} folds the result into
   * the resolution's own `Resolved.warnings` — the SAME channel the CLI outcome line and the COMMS
   * card's `warnings` channel both already render (`decisions/0083`'s "loud, not silent" posture).
   */
  warnings?: string[];
  /**
   * Forced attacks an accepted authored offer queued (`forced_attack` — Rebound's reflection,
   * `npc-features-fielded-roster` Phase 11.1). Carried out through `applyHitDamage` onto
   * `Resolved.forcedAttacks`; the referee resolves them after this resolution settles.
   */
  forcedAttacks?: ForcedAttackRequest[];
};

/** The inert outcome — no reaction fired (declined, ineligible, or no offer). */
const NO_REACTION: ReactionOutcome = { resistant: false, steps: [] };

/**
 * One target-side reaction offer the registry consults at the interception window. A fixed,
 * authored member (never registration-order) so the recorded resolution is byte-identical across
 * replay. `outcome` is pure over `(ctx, answer)`.
 */
type ReactionOffer = {
  /** Stable id — the registry runs fixed by its list order. */
  readonly id: string;
  /** Eligible to fire against this target right now? */
  eligible(ctx: ReactionContext): boolean;
  /**
   * **Which actor must answer** — the prompt's `who`. Brace/an authored target-side offer: the
   * target (the degenerate case, decider === target). A protector-gathered offer (Intercede): the
   * PROTECTOR — a THIRD combatant, neither the attacker nor the target. **Must agree with
   * {@link promptId}'s embedded decider id** — that is what the `<offerId>:<deciderId>` convention
   * exists to keep aligned.
   */
  who(ctx: ReactionContext): string;
  /** The stable prompt id the answer records under (unique per target). */
  promptId(ctx: ReactionContext): string;
  /** The human-readable prompt the decider sees. */
  prompt(ctx: ReactionContext): string;
  /**
   * The reaction's **short name** (`Brace`, an authored feature's reaction name) — the COMMS
   * card's question headline ("Brace against Ronin's hit?") and the durable outcome line
   * (`<unit> · Brace: Brace`) name the decision by it.
   */
  readonly title: string;
  /**
   * An optional **one-line cost summary** the COMMS card and prompt window show under the damage
   * tiles (`comms-attack-reaction-cards` settled item 6): the price the tiles can't show, never
   * a rules paragraph. Keep it to one short line. Absent ⇒ no line.
   */
  readonly summary?: string;
  /** The selectable options (the recorded answer is one of these ids). */
  readonly options: { id: string; label: string }[];
  /** Pure: build the outcome for a recorded answer. A non-firing answer returns {@link NO_REACTION}. */
  outcome(ctx: ReactionContext, answer: string): ReactionOutcome;
};

/**
 * May `id` still use the NAMED reaction `name` now? BOTH its GLOBAL reaction slot must be
 * open ({@link reactionSlotOpen} — one reaction per turn, reopened at every turn start) AND
 * it must not have already used THIS named reaction since its own last turn start ({@link
 * canUseReaction} — the named cap, refreshed at its own turn start, independent of how often the slot refreshes, and independent of any
 * OTHER named reaction `id` also holds) AND its status-keyed reaction legality must be clear
 * ({@link checkReactionStatusLegality} — Jammed/Grappled forbid ALL reactions, Stunned/Down-and-
 * Out's "no actions or reactions" reaches the same conclusion). This is the ONE eligibility choke
 * point every reaction offer routes through (Brace's own `eligible`, `compileProtectorOffers`'s
 * protector gate), so the status wall lands for Brace + any future authored/protector reaction
 * with no separate mechanism — the room's Overwatch declaration reads the SAME
 * `checkReactionStatusLegality` directly (Overwatch never routes through this offer registry).
 *
 * **Held-action lockout** (RAW Core pp. 71-72 — "cannot move, act, or take reactions" while
 * holding): a combatant currently holding a prepared action (`state.activation.heldActions[id]`)
 * may take NO reaction, Brace included. This is one of the lockout's TWO landing sites — the
 * room's Overwatch declaration is the other, since Overwatch never routes through this offer
 * registry either; **both must gate or a held unit can still React**.
 *
 * **Exported** so `checkpointRegistry.ts`'s built-in Overwatch move-checkpoint offer reuses this
 * SAME choke point (named `"overwatch"`, the manual path's own named-cap key, `economy.ts`'s
 * `reactionId: "overwatch"`) rather than re-deriving the slot/cap/status-legality/held-action stack
 * a second time.
 *
 * **`spentReactions` — the in-flight fifth gate** (`area-attack-reaction-offers` Phase 2.3, D5).
 * Every gate above reads `state.activation`, which is PRE-resolution state: an area attack's
 * sub-resolutions all consult the same unmutated snapshot, so a reactor eligible for the first
 * caught target was still eligible for the second even after spending its one reaction on the
 * first. The area fan-out ({@link import("./resolve/attack").resolve}) therefore threads a set of
 * combatant ids that have already spent a reaction EARLIER IN THIS ONE RESOLUTION, filled from
 * each sub-resolution's own `reactionSpentBy`, and this choke point refuses them. Keyed on the
 * combatant, not on `(combatant, name)`, because {@link reactionSlotOpen} — the global slot — is
 * the gate the un-mutated state is lying about: one reaction per turn, whatever its name. Absent
 * (the single-target spine, every non-fan-out caller) ⇒ nothing has been spent in-flight.
 *
 * **`unlimited`** skips only the named cap: a clause authored `frequency: "unlimited"` (Parry) still
 * needs the global slot and every other gate.
 *
 * `name` rides the options object because oxlint caps a function at three parameters; collapsing
 * it there mirrors {@link import("./toHitReactionRegistry")}'s own `turretDeployerAvailable`,
 * which already uses the `(state, id, opts)` shape for the same reason.
 */
export function reactionAvailable(
  state: EncounterState,
  id: string,
  opts: { name: string; unlimited?: boolean; spentReactions?: ReadonlySet<string> },
): boolean {
  return (
    opts.spentReactions?.has(id) !== true &&
    affordable({ kind: "reaction_use", carrier: id, name: opts.name, ...(opts.unlimited === true ? { unlimited: true } : {}) }, state, undefined) &&
    checkReactionStatusLegality(state, id).ok &&
    state.activation.heldActions[id] === undefined
  );
}

/**
 * **Brace** (RAW Core p. 73) — Reaction, 1/round. Offered to a **mech** target (NPCs cannot Brace)
 * that still has its reaction. On `brace`: Resistance to the triggering attack
 * (`resistant: true`) + the durational `Braced` status + spend the defender's reaction + **reveal
 * Hidden** (RAW: "Hidden is lost if you… take a Reaction" — a reaction is a reaction whether or not
 * it fires an attack of its own; Overwatch reveals via the shared post-attack observer since it IS
 * an attack, but Brace produces no `to_hit`/`save` step of its own, so it needs its own explicit
 * clear). On `decline`: nothing (recorded so replay reproduces the choice) — a DECLINED offer never
 * fired the reaction, so it doesn't reveal.
 */
const BRACE_OFFER: ReactionOffer = {
  id: "brace",
  eligible: (ctx) => ctx.target.kind === "mech" && reactionAvailable(ctx.state, ctx.target.id, { name: "brace", ...(ctx.spentReactions ? { spentReactions: ctx.spentReactions } : {}) }),
  who: (ctx) => ctx.target.id,
  promptId: (ctx) => reactionPromptId("brace", ctx.target.id),
  prompt: () =>
    "Brace? Resistance to all damage, heat, and burn from this attack, and +1 Difficulty to all other attacks against you until the end of your next turn.",
  title: "Brace",
  // The PRICE, per RAW Core p.73 ("you cannot take reactions until the end of your next turn and
  // on that turn, you can only take one quick action"). The +1 Difficulty clause is a benefit, and
  // the Resistance is what the tiles already show. Both halves are engine-enforced
  // (`checkReactionStatusLegality` and `onBracedTurn`, `BRACED`'s note in `effects/seedEntries.ts`);
  // the line says them so the player weighs the cost before answering.
  summary: "Uses your reaction · next turn: 1 quick action, no reactions",
  options: [
    { id: BRACE_OPTION, label: "Brace" },
    { id: DECLINE_OPTION, label: "Decline" },
  ],
  outcome: (ctx, answer) => {
    if (answer !== BRACE_OPTION) return NO_REACTION;
    // Idempotent (mirrors the add_status atom): don't re-add a Braced the target already carries.
    const steps: ResolvedStep[] = [];
    if (!ctx.target.statuses.some((s) => s.name === BRACED_STATUS)) {
      const status: StatusAddStep = {
        op: "status_add",
        target: ctx.target.id,
        status: BRACED_STATUS,
        expiry: { kind: "turn", edge: "end", who: "target" }, // removed at the end of the bracer's (carrier's) next turn
        source: ctx.target.id, // the bracer applies it to itself
        // STAMP this one: RAW's own prompt text (above) says "until the end of your next turn,"
        // genuinely next, and it's reachable mid-the-bracer's-OWN-turn — an Overwatch-triggered
        // reaction attack against a moving mech resolves during that mover's own turn, and the
        // mover can Brace against it, the exact same-turn coincidence the `tables.ts` stamps guard
        // against.
        appliedTurn: ctx.state.activation.turnOrdinal,
      };
      steps.push(status);
    }
    // Reveal Hidden on taking the reaction (idempotent — clearHiddenSteps is a no-op if the
    // bracer isn't Hidden, so the common non-Hidden case emits nothing extra).
    steps.push(...clearHiddenSteps(ctx.target));
    const riders = braceRiders(ctx);
    steps.push(...riders.steps);
    return { resistant: true, steps, spendReactionBy: [{ combatantId: ctx.target.id, name: "brace" }], ...(riders.warnings.length > 0 ? { warnings: riders.warnings } : {}) };
  },
};

/**
 * What the bracer's own content does "when you Brace" (Reactive Weave): its `action`-family clauses
 * matching `actionId: "brace"`, fired with the bracer as `self` and `target`. Interactive, a clause
 * that needs an answer (Reactive Weave's `move_spaces`) halts and asks the bracer, and the answer
 * rides the attack's `choices` back in.
 */
function braceRiders(ctx: ReactionContext): { steps: ResolvedStep[]; warnings: string[] } {
  const overlay = ctx.overlay ?? SEED_OVERLAY;
  const handlers = gatherHandlersAt({ state: ctx.state, carrierId: ctx.target.id, overlay, family: "action" });
  if (handlers.length === 0) return { steps: [], warnings: [] };
  const choices = ctx.interactive ? promptingChoices(ctx.choices) : recordedChoices(ctx.choices);
  const fired = fireTrigger("action", staticHandlers(handlers), {
    state: ctx.state,
    primaryTarget: ctx.target.id,
    dice: ctx.dice,
    overlay,
    event: { family: "action", actionId: "brace" },
    ...(ctx.interactive ? { interactive: true } : {}),
    ...(choices !== undefined ? { choices } : {}),
  });
  return { steps: fired.steps, warnings: fired.warnings };
}

/** The recorded-answer option id an authored (content) reaction offer accepts with. */
export const AUTHORED_ACCEPT_OPTION = "use";

/**
 * Pull any `reduce_pending_damage` atom out of an effect-atom list — `atoms.ts`'s doc comment
 * reserves this atom for the reaction window alone, so it is never handed to the generic
 * {@link interpretEffect} path; this is the one place that reads
 * it. Rolls its dice through the SAME recorded `DiceSource` the rest of the offer's atoms would
 * draw through (replay-stable, in the atom-list's authored order), sums every occurrence (additive
 * — an entry could in principle stack more than one, though no seed does), and returns the
 * remaining atoms unchanged for the caller to interpret normally. A top-level scan only (mirrors
 * `extractPendingDamageTransform`'s sibling below) — nested inside an `if`/`choose` branch is not
 * read; v1's authored content never nests it.
 */
function extractReducePendingDamage(
  atoms: readonly EffectAtom[],
  dice: DiceSource,
): { reduce: number; rest: EffectAtom[] } {
  let reduce = 0;
  const rest: EffectAtom[] = [];
  for (const a of atoms) {
    if (a.op === "reduce_pending_damage") {
      const r = rollDamage({ source: dice, count: a.dice.count, sides: a.dice.sides, crit: false });
      const rolled = r.draws.filter((_, i) => r.kept[i]).map((d) => d.value).reduce((x, y) => x + y, 0);
      reduce += rolled + (a.bonus ?? 0);
    } else {
      rest.push(a);
    }
  }
  return { reduce, rest };
}

/** The window's {@link DamageEvent}: the attack's `ranged` fact, and `tech`/`hit` when the caller set them. */
function damageEvent(ctx: ReactionContext): DamageEvent {
  return { family: "damage", ranged: ctx.ranged, ...(ctx.tech !== undefined ? { tech: ctx.tech } : {}), ...(ctx.hit !== undefined ? { hit: ctx.hit } : {}), ...(ctx.dtypes !== undefined ? { dtypes: ctx.dtypes } : {}) };
}

/**
 * Compile the target's active-status **AND** npc-feature-vector `on_damage_taken` `optional: true`
 * clauses into {@link ReactionOffer}s the SAME shape {@link BRACE_OFFER} is — so
 * {@link gatherReaction} can append them after the built-ins with no special-casing. Gathered via
 * {@link gatherStatusHandlers} (the generic any-trigger-point status scan, `loader.ts`) THEN
 * {@link gatherPilotHandlers} over {@link combatantEffectIds}`(pilotEffectIds, frameEffectIds,
 * pilotGearEffectIds, npcFeatureEffectIds)` — status THEN pilot, the canonical `CARRIER_VECTORS`
 * order (`decisions/0166`), mirroring {@link compileAutomaticOutcome}'s own composition — filtered
 * to `on_damage_taken` + `optional: true` (a non-optional `on_damage_taken` clause is an
 * *automatic* consequence, not a reaction offer — {@link compileAutomaticOutcome} owns those).
 * Each clause's own `condition` (if any) is evaluated eagerly here (against a plain
 * attacker/target predicate context) so an ineligible clause never becomes an offer at all.
 *
 * The prompt id is `<carrierId>#<clauseIndex>:<targetId>` — `idx` is this gather's stable,
 * replay-safe position in the COMBINED status-then-pilot handler list, **not** the status name.
 * Status handlers keep the SAME indices they always have (appended first, unchanged count for
 * every shipped status-borne offer today — `reactionRegistry.test.ts`'s "Leadership Die" pin), so
 * widening this gather never shifts an already-recorded prompt id.
 *
 * **Widened `npc-features-fielded-roster` Phase 7.2 — no longer status-only.** Before this, a
 * reaction offer needed `Handler.name`/`.partner`/`.carrierSource` — fields ONLY the status
 * vector's gather threaded (`gatherStatusHandlers`'s own doc) — so composing in pilot/system
 * handlers here would have silently widened WHICH content can become a reaction offer without
 * those fields ever being populated. That gap is closed for `.name`: `handlersFromRefs`
 * (`loader.ts`) now folds `ref.name` (the pilot-effect/npc-feature's own overlay-entry id) onto
 * every pilot/system handler too, which is what gives a widened offer here a real per-feature
 * reaction-budget key (Rebound's carrier — Hunker Down fires on the SEPARATE attack outcome,
 * `toHitReactionRegistry.ts`, Phase 7.3). `.partner`/`.carrierSource` stay status-only (still
 * `undefined` off a pilot handler) — no npc-feature reaction authored so far needs either.
 *
 * **A pilot/npc-feature-vector offer is a genuine RAW Reaction (1/round), unlike the status-borne
 * offers above it in this same list.** No shipped status-borne `on_damage_taken` offer (Inspiring
 * Presence's Leadership Die, Argonaut Shield, Intercede/Bondmate) is RAW-tagged Reaction — each is
 * gated by its own resource (a Die count, a standing bond), never the reaction economy, so those
 * offers correctly carry NO `reactionAvailable` gate and NO `spendReactionBy` (unchanged by this
 * widening — see the equality pins in `reactionRegistry.test.ts`). A pilot-vector offer DOES gate
 * on {@link reactionAvailable} (skipped at compile time, mirroring this function's existing
 * "already filtered at compile time" posture) and spends the reaction on accept, keyed by
 * `reactionName`: the handler's own `name` (the feature id, per the fold above, Q3(b)), so a
 * clause `name` (Rebound's) titles the offer without moving its budget key. A status-borne offer
 * keys by the clause's `name`, else the handler's, else the carrier id.
 *
 * **A weapon-picked `forced_attack`** (`ll1-granted-reactions` S3, Return Fire) replaces Use with
 * one option per weapon {@link eligiblePickWeapons} allows (id = the instance key, label = its
 * name), and compiles no offer when none is eligible. The answer is the weapon the attack fires.
 * A body that is a single `choose` (Deflect) likewise offers the options whose `when` holds.
 */
function compileAuthoredOffers(ctx: ReactionContext): ReactionOffer[] {
  const overlay = ctx.overlay ?? SEED_OVERLAY;
  const statusHandlers: Handler[] = gatherStatusHandlers(ctx.state, ctx.target.id, overlay);
  const pilotHandlers: Handler[] = gatherPilotHandlers({
    carrierId: ctx.target.id,
    pilotEffectIds: combatantEffectIds(ctx.target),
    overlay,
    family: "damage",
  });
  const handlers: Handler[] = [...statusHandlers, ...pilotHandlers];
  const pctx: PredicateContext = {
    state: ctx.state,
    actors: { self: ctx.target.id, target: ctx.target.id, attacker: ctx.attacker.id },
    // `npc-features-fielded-roster` Phase 7.2 — a real `DamageEvent` at last, so `match: {
    // family: "damage", ranged: true }` (Rebound's trigger) is genuinely reachable, not
    // structurally inert (`DamageMatch`'s own doc).
    event: damageEvent(ctx),
  };
  const out: ReactionOffer[] = [];
  handlers.forEach((h, idx) => {
    // A `protector: true` handler is the OTHER gather direction's vocabulary
    // (`compileProtectorOffers`) — it fires "when my bound partner takes damage," never "when I
    // do." Excluding it here matters in practice: a MUTUAL relationship stamp (Bondmate bound on
    // both sides) would otherwise have the TARGET's own copy of the handler misread as if the
    // target were offering to reduce its own damage, shadowing the real protector offer.
    if (h.effect.on !== "damage" || h.effect.optional !== true || h.effect.protector === true) return;
    // This is a bespoke gate (NOT `fireTrigger`), so it evaluates `match` itself too — see
    // `checkpointRegistry.ts`'s identical gate. `damage` carries a REAL `DamageEvent` here as of
    // `npc-features-fielded-roster` Phase 7.2 (`pctx.event` above) — `match: { family: "damage",
    // ranged: true }` (Rebound's own trigger) is genuinely reachable; every OTHER damage-family
    // fact stays unbacked (`DamageMatch`'s own doc).
    if (h.effect.match && !evaluateMatch(h.effect.match, pctx.event)) return;
    if (h.effect.condition && !evaluatePredicate(h.effect.condition, pctx)) return;
    // Beyond `statusHandlers.length` ⇒ gathered off the pilot/npc-feature vector — the genuine
    // Reaction-economy carrier this function's own doc explains.
    const isPilotVector = idx >= statusHandlers.length;
    // A pilot/npc-feature offer is budgeted by its feature id (`h.name`, Q3(b)) even when the
    // clause names itself: that `name` is Rebound's display title, not a second economy key.
    const reactionName = isPilotVector ? (h.name ?? h.effect.name ?? h.source) : (h.effect.name ?? h.name ?? h.source);
    if (isPilotVector && !reactionAvailable(ctx.state, ctx.target.id, { name: reactionName, ...(h.effect.frequency === "unlimited" ? { unlimited: true } : {}), ...(ctx.spentReactions ? { spentReactions: ctx.spentReactions } : {}) })) return;
    // `ll1-granted-reactions` S3: a weapon-picked `forced_attack` (Return Fire) offers one option
    // per eligible weapon instead of Use, and no offer at all when nothing is eligible.
    const pick = findWeaponPick(h.effect.effect);
    let accept = [{ id: AUTHORED_ACCEPT_OPTION, label: "Use" }];
    if (pick !== undefined) {
      const actors = { self: ctx.target.id, target: ctx.target.id, attacker: ctx.attacker.id };
      const reactor = resolveActorId(pick.attacker ?? "self", actors);
      const victim = resolveActorId(pick.target ?? "target", actors);
      const weapons = reactor !== undefined && victim !== undefined ? eligiblePickWeapons({ state: ctx.state, attacker: reactor, target: victim, pick: pick.weapon.pick }) : [];
      if (weapons.length === 0) return;
      accept = weapons.map((w) => ({ id: weaponInstanceKey(w), label: w.name }));
    }
    // A body that is one `choose` (Deflect's dice count) offers its open options in place of Use,
    // and no offer when none is open. The answer runs that option's effect.
    const only = h.effect.effect.length === 1 ? h.effect.effect[0] : undefined;
    const choice = only?.op === "choose" ? only.options.filter((o) => o.when === undefined || evaluatePredicate(o.when, pctx)) : undefined;
    if (choice !== undefined) {
      if (choice.length === 0) return;
      accept = choice.map((o) => ({ id: o.id, label: o.label ?? o.id }));
    }
    const offerId = `${h.source}#${idx}`;
    out.push({
      id: offerId,
      eligible: () => true, // already filtered (active status/pilot-effect + condition + reaction budget) at compile time.
      who: (c) => c.target.id, // target-class offer — decider IS the target (Brace's degenerate case).
      promptId: (c) => reactionPromptId(offerId, c.target.id),
      // A named clause says which reaction is on offer ("Rebound"); an unnamed one keeps the carrier.
      prompt: (c) => (h.effect.name !== undefined ? `${c.target.name}: use ${h.effect.name} against this damage?` : `Use ${h.source}'s reaction to this damage?`),
      // The clause's own name, else the carrier the prompt already names — never the feature-id
      // fallback `reactionName` can land on, which is an economy key, not display text.
      title: h.effect.name ?? h.source,
      options: [...accept, { id: DECLINE_OPTION, label: "Decline" }],
      outcome: (c, answer) => {
        if (!accept.some((o) => o.id === answer)) return NO_REACTION;
        // Pull any `reduce_pending_damage` atom out of the effect list BEFORE the generic
        // interpreter sees it — it's a reaction-window-only atom (`atoms.ts`'s doc comment),
        // rolled here through the SAME recorded `DiceSource` so the draw is replay-stable, keyed
        // into the offer's own outcome rather than a grounded step of its own.
        const body = choice?.find((o) => o.id === answer)?.effect ?? h.effect.effect;
        const { reduce, rest } = extractReducePendingDamage(body, c.dice);
        const forcedAttacks: ForcedAttackRequest[] = [];
        const effectCtx: EffectContext = {
          state: c.state,
          actors: { self: c.target.id, target: c.target.id, attacker: c.attacker.id },
          dice: c.dice,
          overlay,
          // `npc-features-fielded-roster` Phase 7.1: lets a `resist_pending_damage` atom
          // ANYWHERE in `rest` (including nested under `if`/`roll` — Rebound's "Use → roll 1d6 →
          // on 4+ resist") know its flag will actually be read. Threaded for every offer this
          // function builds, status-borne included — none of today's shipped status content
          // authors this atom, so it's a no-op for them.
          pendingDamageWindow: true,
          // `npc-features-fielded-roster` Phase 11.1: Rebound's reflection queues here.
          forcedAttacks,
          ...(c.triggeringAttack !== undefined ? { triggeringAttack: c.triggeringAttack } : {}),
          ...(pick !== undefined ? { pickedWeapon: answer } : {}),
        };
        const r = interpretEffect(rest, effectCtx);
        return {
          resistant: r.pendingDamage?.resist === true,
          steps: r.steps,
          ...(reduce > 0 ? { reduce } : {}),
          ...(forcedAttacks.length > 0 ? { forcedAttacks } : {}),
          ...(isPilotVector ? { spendReactionBy: [{ combatantId: c.target.id, name: reactionName }] } : {}),
          // `npc-features-fielded-roster` Phase 7.4: an authored `advisory` atom nested in this
          // offer's `effect` (Rebound's own reflection warning) would otherwise be silently
          // dropped — `ReactionOutcome` had no `warnings` field until this phase. See that
          // field's own doc for the full thread into `Resolved.warnings`.
          ...(r.warnings.length > 0 ? { warnings: r.warnings } : {}),
        };
      },
    });
  });
  return out;
}

/**
 * Pull a `redirect_pending_damage` atom out of an effect-atom list — the protector-gather sibling
 * of {@link extractReducePendingDamage}: a bare marker (no dice, no bonus), so this just reports
 * whether it's present and returns the rest unchanged for the caller to interpret normally.
 */
export function hasRedirectPendingDamage(atoms: readonly EffectAtom[]): { redirect: boolean; rest: EffectAtom[] } {
  const rest = atoms.filter((a) => a.op !== "redirect_pending_damage");
  return { redirect: rest.length !== atoms.length, rest };
}

/**
 * Compile the **protector-gather** direction — at the interception window, ALSO gather authored
 * `on_damage_taken` offers off OTHER placed combatants' overlays (Intercede, carried by the
 * Bondmate relationship) whose handler declares `protector: true` AND whose bound `partner`
 * (`StatusInstance.partner`) names `ctx.target` — i.e. "this OTHER combatant's marker points at
 * the combatant that's currently taking damage." Walked in a **fixed deterministic order**
 * (combatant ids ascending, `Object.keys(...).sort()`), skipping the damaged target itself and any
 * unplaced combatant (no position ⇒ no adjacency/LoS to measure).
 *
 * Each candidate handler is **condition-gated** before it ever becomes an offer:
 *   1. **adjacency** to the damaged target ({@link combatantRange} === 1 — "you are adjacent to
 *      your Bondmate"),
 *   2. **line of sight** to the attack's source ({@link combatantLineOfSight} — "damage from a
 *      source within your line of sight"),
 *   3. the protector's own **reaction available** ({@link reactionAvailable}, keyed on the
 *      handler's own entity id — Intercede's 1/round cap, independent of Brace's),
 *   4. the handler's own authored `condition` (if any), evaluated eagerly.
 * A protector failing any gate is silently skipped for THIS hit (never an offer) — RAW's
 * trigger condition is a precondition on the reaction existing at all, not a decline.
 *
 * **The prompt id carries the PROTECTOR's id** (`<offerId>:<protectorId>`), NOT the target's —
 * the load-bearing half of the `<offerId>:<deciderId>` convention: the backend's `who`-keyed
 * routing + the `reactionDefenderId` attribution guard both key off whatever id the prompt embeds,
 * so a protector-gathered offer's decider is correctly the protector (the third routing case —
 * neither the attacker nor the target). {@link ReactionOffer.who} agrees.
 *
 * **Deliberately status-only, NOT `gatherHandlersAt`** — same rationale as {@link
 * compileAuthoredOffers}'s own doc: this gather leans on `Handler.partner` (the bound-partner
 * check just below), a field only the status vector threads.
 */
function compileProtectorOffers(ctx: ReactionContext): ReactionOffer[] {
  const overlay = ctx.overlay ?? SEED_OVERLAY;
  const candidates = protectorCandidates({
    state: ctx.state,
    targetId: ctx.target.id,
    attackerId: ctx.attacker.id,
    sees: (protectorId) => combatantLineOfSight(ctx.state, protectorId, ctx.attacker.id),
    // `npc-features-fielded-roster` Phase 7.2 — see `compileAuthoredOffers`'s identical thread.
    event: damageEvent(ctx),
    overlay,
    ...(ctx.spentReactions ? { spentReactions: ctx.spentReactions } : {}),
  });
  return candidates.map(({ protector, h, idx, reactionName }): ReactionOffer => {
    const protectorId = protector.id;
    const offerId = `${h.source}#${idx}`;
    return {
      id: offerId,
      eligible: () => true, // already filtered (relationship + spatial + reaction budget) at compile time.
      who: () => protectorId, // the THIRD routing case — decider is neither attacker nor target.
      promptId: () => reactionPromptId(offerId, protectorId),
      prompt: () => `${protector.name} may Intercede — take ${ctx.target.name}'s damage instead?`,
      title: "Intercede", // the prompt's own verb, for every protector offer
      options: [
        { id: AUTHORED_ACCEPT_OPTION, label: "Use" },
        { id: DECLINE_OPTION, label: "Decline" },
      ],
      outcome: (c, answer) => {
        if (answer !== AUTHORED_ACCEPT_OPTION) return NO_REACTION;
        const { redirect, rest } = hasRedirectPendingDamage(h.effect.effect);
        const effectCtx: EffectContext = {
          state: c.state,
          actors: { self: protectorId, target: protectorId, attacker: c.attacker.id },
          dice: c.dice,
          overlay,
          // `npc-features-fielded-roster` Phase 7.1 — see `compileAuthoredOffers`'s identical
          // thread for why: lets a nested `resist_pending_damage` atom know its flag will be
          // read. No shipped protector-gathered content authors it today.
          pendingDamageWindow: true,
        };
        const r = interpretEffect(rest, effectCtx);
        return {
          resistant: r.pendingDamage?.resist === true,
          steps: r.steps,
          spendReactionBy: [{ combatantId: protectorId, name: reactionName }],
          ...(redirect ? { redirect: { partnerId: protectorId } } : {}),
          // `npc-features-fielded-roster` Phase 7.4 — see `compileAuthoredOffers`'s identical
          // thread; no shipped protector-gathered content authors `advisory` today.
          ...(r.warnings.length > 0 ? { warnings: r.warnings } : {}),
        };
      },
    };
  });
}

/** One protector whose bound clause passed every gate {@link protectorCandidates} applies. */
export type ProtectorCandidate = {
  protector: Combatant;
  h: Handler;
  /** The clause's index in the protector's status-handler list: the offer id's `#<idx>`. */
  idx: number;
  /** The reaction's named-cap key: the clause's own `name` (`intercede`), else the carrier's. */
  reactionName: string;
};

/**
 * The protector gates shared by the attack window ({@link compileProtectorOffers}) and the
 * non-attack one (`nonAttackIntercept.ts`): every placed combatant other than the target, in id
 * order, whose status clause is `on:"damage"`, `optional` and `protector`, whose `partner` is the
 * target, which is adjacent to the target, which `sees` the damage's source, whose reaction is
 * available, and whose `match`/`condition` hold. The two windows differ only in the source they
 * measure line of sight to and the event they match against.
 */
export function protectorCandidates(args: {
  state: EncounterState;
  targetId: string;
  /** Bound as the predicate context's `attacker`: the attacker, or a non-attack source combatant. */
  attackerId?: string;
  /** Does this protector have line of sight to the damage's source? */
  sees: (protectorId: string) => boolean;
  event: DamageEvent;
  overlay: LoadedOverlay;
  spentReactions?: ReadonlySet<string>;
}): ProtectorCandidate[] {
  const { state, targetId, attackerId, sees, event, overlay, spentReactions } = args;
  const out: ProtectorCandidate[] = [];
  for (const protectorId of Object.keys(state.combatants).sort()) {
    if (protectorId === targetId) continue;
    const protector = state.combatants[protectorId];
    if (!protector?.pos) continue; // unplaced — no adjacency/LoS to measure.
    const handlers: Handler[] = gatherStatusHandlers(state, protectorId, overlay);
    const pctx: PredicateContext = {
      state,
      actors: { self: protectorId, target: targetId, ...(attackerId !== undefined ? { attacker: attackerId } : {}) },
      event,
    };
    handlers.forEach((h, idx) => {
      if (h.effect.on !== "damage" || h.effect.optional !== true || h.effect.protector !== true) return;
      if (h.partner !== targetId) return; // this protector's bond doesn't point at the damaged combatant.
      if (combatantRange(state, protectorId, targetId) !== 1) return; // not adjacent to the damaged target.
      if (!sees(protectorId)) return; // no LoS to the source.
      const reactionName = h.name ?? h.source;
      if (!reactionAvailable(state, protectorId, { name: reactionName, ...(spentReactions ? { spentReactions } : {}) })) return; // the protector's own 1/round cap.
      // Bespoke gate, so `match` is evaluated here too — see `compileAuthoredOffers` above.
      if (h.effect.match && !evaluateMatch(h.effect.match, pctx.event)) return;
      if (h.effect.condition && !evaluatePredicate(h.effect.condition, pctx)) return;
      out.push({ protector, h, idx, reactionName });
    });
  }
  return out;
}

/**
 * Gather + apply the target's `on_damage_taken` **automatic** (non-optional) effects — Argonaut
 * Shield's split, the sibling of {@link compileAuthoredOffers}'s optional-offer
 * compiler for the case that needs NO prompt at
 * all: "automatic while the pairing holds." Filtered to `on_damage_taken` + `optional !== true`, each clause's
 * `condition` (if any) evaluated eagerly. A clause carrying `split_pending_damage` resolves its
 * partner off the carrying status INSTANCE's `partner` (`Handler.partner`, populated by
 * `gatherStatusHandlers` from `StatusInstance.partner`) — NOT an atom field, since the pairing is
 * the status's own relationship. Adjacency is **re-checked at fire time** ("lasts until your
 * target breaks adjacency"): an unplaced/unbound/non-adjacent partner makes the split a no-op for
 * THIS hit (the standing status itself is separately cleared by the post-move recomputer,
 * `spatialRecompute.ts`, when adjacency breaks — this is defense in depth, not the only gate). Any
 * OTHER atoms alongside `split_pending_damage` (none in the seed) still interpret normally.
 *
 * **`carrierSource` is threaded PER HANDLER, not once for the whole gather**: each `Handler`
 * carries its OWN `carrierSource` (`gatherStatusHandlers`/`loader.ts`, stamped from that
 * handler's own carrying `StatusInstance.source`), and a `has_status` with `source: "carrier"` condition
 * (`predicate.ts`) must evaluate against THAT handler's source — never a single value shared
 * across the whole loop, which misfires the moment two different casters' handlers are gathered
 * together (Covering Fire's "Watcher" companion status is the live case: caster A's own
 * `on_damage_taken` self-clear must read A's own `carrierSource`, not some other caster's).
 *
 * **NOT status-only any more — the gather is status THEN pilot** (`decisions/0166`,
 * `fielded-hardsuit-raw-gaps` Phase 1). {@link gatherPilotHandlers} over
 * {@link combatantEffectIds}`(pilotEffectIds, frameEffectIds, pilotGearEffectIds)` is composed
 * AFTER the status gather, so the canonical status → pilot order `CARRIER_VECTORS` lists (and
 * `resolveMoveTrigger` preserves) holds here too. Before it, an `on: "damage"` clause authored
 * onto ANY pilot-gear item, frame trait, talent, or core bonus parsed, linted, and shipped a green
 * entry test **while never firing** — the fielded instance being `pg_stealth_hardsuit`, whose RAW
 * "they cease to be Invisible if they take any damage" clause had nowhere to live. Hand-composed,
 * NOT {@link import("./effects").gatherHandlersAt} — this is a narrow per-vector widening at one
 * site, not the `decisions/0112` §7 pipeline joint.
 *
 * **The three sibling gathers stay status-only ON PURPOSE — do NOT "tidy" them into consistency
 * with this one.** {@link compileAuthoredOffers}, {@link compileProtectorOffers}, and
 * `toHitReactionRegistry.ts`'s `compileValiantAidOffers` are **offer** compilers: an offer needs
 * `Handler.name`/`.partner`/`.carrierSource` (the reaction-budget key, the bound partner, the
 * third-party caster), and only the status vector threads those fields, so composing pilot/system
 * handlers in there would silently widen which content can become a reaction *offer* while those
 * fields go unpopulated. Their `Deliberately status-only` comments are load-bearing. This function
 * is **not** an offer compiler — it mints no prompt, no reaction-budget spend, no partner binding —
 * and it already spreads `carrierSource` conditionally, so a pilot handler carrying none (the
 * pilot vector never populates it) degrades correctly rather than misevaluating a predicate.
 *
 * **Coverage envelope.** This function is reached from {@link gatherReaction} (the hit arm) and
 * {@link gatherDamageNotification} (the two attack hits that open no reaction window: a Reliable
 * floor on a miss, and Sniper's Mark's structure substitution). Both early-return unless
 * `activation.turn.phase === "acting"`, so it covers **attack damage during sequenced combat**.
 * Non-attack damage reaches the same clauses through a separate notification site on the trigger
 * bus (`resolve/triggers.ts`'s `resolveDamageTrigger`, `decisions/0175`), which carries its own
 * site audit; the two site sets are disjoint, so a clause never fires twice for one damage
 * instance. Attack damage taken outside a turn still fires nothing.
 */
function compileAutomaticOutcome(ctx: ReactionContext): { steps: ResolvedStep[]; warnings: string[]; split?: { partnerId: string }; spendReactionBy: NonAttackReactionSpend[]; named: string[] } {
  const overlay = ctx.overlay ?? SEED_OVERLAY;
  const steps: ResolvedStep[] = [];
  const warnings: string[] = [];
  // A `damage` atom's Intercede (Spikes hitting a Bondmate) asks with the window's answers.
  // Its uses settle here, on a ledger of their own, and the window raises them on the attack's.
  const intercede: InterceptSink = { answers: ctx.choices, interactive: ctx.interactive, ledger: reactionLedger(undefined) };
  let split: { partnerId: string } | undefined;
  const named: string[] = [];
  for (const h of automaticDamageHandlers(ctx, overlay)) {
    // A `dealt`-matching clause reads the SETTLED damage, which does not exist yet at this
    // pre-mitigation point — it runs in {@link gatherSettledDamageOutcome} instead.
    if (isSettledDamageClause(h.effect)) continue;
    const pctx = automaticPredicateContext(ctx, h, {});
    // Bespoke gate, so `match` is evaluated here too — see `compileAuthoredOffers` above.
    if (h.effect.match && !evaluateMatch(h.effect.match, pctx.event)) continue;
    if (h.effect.condition && !evaluatePredicate(h.effect.condition, pctx)) continue;
    const rest = h.effect.effect.filter((a) => a.op !== "split_pending_damage");
    const hasSplit = rest.length !== h.effect.effect.length;
    if (hasSplit && h.partner !== undefined) {
      const partner = ctx.state.combatants[h.partner];
      const adjacent =
        partner !== undefined &&
        !!partner.pos &&
        !!ctx.target.pos &&
        combatantRange(ctx.state, ctx.target.id, h.partner) === 1;
      if (adjacent) split = { partnerId: h.partner };
    }
    if (rest.length > 0) {
      const r = interpretEffect(rest, { ...automaticEffectContext(ctx, h, overlay), intercede });
      steps.push(...r.steps);
      if (r.steps.length > 0) named.push((h.effect.name ?? h.entityId ?? h.name ?? h.source)!);
      // `npc-features-fielded-roster` Phase 13.1 — the same thread Phase 7.4 gave
      // `compileAuthoredOffers`: an automatic clause's `advisory` (Aggression's) was dropped here.
      warnings.push(...r.warnings);
    }
  }
  return { steps, warnings, spendReactionBy: settleEvents(intercede.ledger).reactions ?? [], named, ...(split ? { split } : {}) };
}

/**
 * **The notification-only pass** for attack damage that opens no reaction window: a RELIABLE
 * weapon's floor on a miss, and Sniper's Mark's structure substitution
 * (`resolve/damageApply.ts`). Runs the target's automatic `on:"damage"` clauses at the same
 * pre-mitigation point {@link gatherReaction} does, so an immunity-nullified hit still clears
 * Personal Shield, and nothing else: no offers (Brace and Return Fire trigger on a hit, and an
 * authored damage-reduce offer needs pending damage this pass does not model), and no protector
 * split (Argonaut Shield's `split` is dropped, since there is no pending damage to split). Pair it
 * with {@link gatherSettledDamageOutcome} after routing, as the hit arm does.
 */
export function gatherDamageNotification(ctx: ReactionContext): { steps: ResolvedStep[]; warnings: string[]; named?: string[] } {
  if (ctx.state.activation.turn.phase !== "acting") return { steps: [], warnings: [] };
  const { steps, warnings, named } = compileAutomaticOutcome(ctx);
  return { steps, warnings, named };
}

/** The target's automatic `on:"damage"` candidates, status THEN pilot vector (see {@link compileAutomaticOutcome}). */
function automaticDamageHandlers(ctx: ReactionContext, overlay: LoadedOverlay): Handler[] {
  const handlers: Handler[] = [
    ...gatherStatusHandlers(ctx.state, ctx.target.id, overlay),
    ...gatherPilotHandlers({
      carrierId: ctx.target.id,
      pilotEffectIds: combatantEffectIds(ctx.target),
      overlay,
      family: "damage",
    }),
  ];
  // Defense in depth: a `protector: true` handler is the protector-gather direction's
  // vocabulary, never this target-class automatic gather — see `compileAuthoredOffers`'s
  // identical exclusion for why a mutual relationship stamp needs it.
  return handlers.filter((h) => h.effect.on === "damage" && h.effect.optional !== true && h.effect.protector !== true);
}

/**
 * Does this clause gate on the SETTLED damage (`match.dealt`)? Such a clause is evaluated after the
 * hit is mitigated, never at the pre-mitigation window. `npc-features-fielded-roster` Phase 13.2.
 */
function isSettledDamageClause(effect: Handler["effect"]): boolean {
  return effect.match?.family === "damage" && effect.match.dealt !== undefined;
}

function automaticPredicateContext(ctx: ReactionContext, h: Handler, settled: { dealt?: boolean; dtypes?: readonly DamageType[] }): PredicateContext {
  return {
    state: ctx.state,
    actors: { self: ctx.target.id, target: ctx.target.id, attacker: ctx.attacker.id },
    // `npc-features-fielded-roster` Phase 7.2 — see `compileAuthoredOffers`'s identical thread.
    event: { ...damageEvent(ctx), ...(settled.dealt !== undefined ? { dealt: settled.dealt } : {}), ...(settled.dtypes !== undefined ? { dtypes: settled.dtypes } : {}) },
    ...(h.carrierSource !== undefined ? { carrierSource: h.carrierSource } : {}),
  };
}

function automaticEffectContext(ctx: ReactionContext, h: Handler, overlay: LoadedOverlay): EffectContext {
  return {
    state: ctx.state,
    actors: { self: ctx.target.id, target: ctx.target.id, attacker: ctx.attacker.id },
    dice: ctx.dice,
    overlay,
    ...(h.carrierSource !== undefined ? { carrierSource: h.carrierSource } : {}),
    // The clause's name, as the trigger bus records it, so a line it writes can say which feature fired.
    ...(h.effect.name ?? h.entityId ?? h.name ? { firing: { name: (h.effect.name ?? h.entityId ?? h.name)!, carrier: h.source } } : {}),
  };
}

/** What {@link gatherSettledDamageOutcome} produced. */
export type SettledDamageOutcome = {
  steps: ResolvedStep[];
  warnings: string[];
  /** Forced attacks the clauses queued (`forced_attack`), executed by the referee after the resolution settles. */
  forcedAttacks: ForcedAttackRequest[];
  /** The cap uses the fired clauses consumed, for the attack to raise with its own (BC-012). */
  consumedCaps: { carrier: string; key: string }[];
};

/**
 * **The settled-damage pass** (`npc-features-fielded-roster` Phase 13.2, Open fork F3). Runs the
 * target's automatic `on:"damage"` clauses that match on `dealt` — AFTER every damage instance has
 * been mitigated, so `dealt` is a real fact: `true` when the target's own final damage (summed
 * over its non-immune `damage` steps) is above 0. Aggression's "after taking damage" is the
 * motivating case: a hit fully absorbed by Armor or Resistance does not burn its once-per-round
 * latch.
 *
 * Same envelope as {@link gatherReaction} (sequenced combat; the hit arm, plus the Reliable-miss and
 * Sniper's Mark arms beside {@link gatherDamageNotification}) and the same status-then-pilot gather
 * as {@link compileAutomaticOutcome}. It never prompts on its own, but a
 * `choose` inside a body can still halt through the ordinary signal.
 *
 * `forced_attack` atoms queue onto {@link SettledDamageOutcome.forcedAttacks}; nothing here
 * resolves them — the referee does, against the post-resolution state (`referee/forcedAttacks.ts`).
 */
export function gatherSettledDamageOutcome(ctx: ReactionContext, settled: { dealt: boolean; dtypes?: readonly DamageType[] }): SettledDamageOutcome {
  const out: SettledDamageOutcome = { steps: [], warnings: [], forcedAttacks: [], consumedCaps: [] };
  if (ctx.state.activation.turn.phase !== "acting") return out;
  const overlay = ctx.overlay ?? SEED_OVERLAY;
  for (const h of automaticDamageHandlers(ctx, overlay)) {
    if (!isSettledDamageClause(h.effect)) continue;
    const pctx = automaticPredicateContext(ctx, h, settled);
    if (h.effect.match && !evaluateMatch(h.effect.match, pctx.event)) continue;
    if (h.effect.condition && !evaluatePredicate(h.effect.condition, pctx)) continue;
    // A capped clause gates and spends the same way the bus does (`resolveDamageTrigger`, BC-012).
    const cap = h.effect.consumeResourceOnApply;
    if (cap !== undefined) {
      const key = resourceCapRawKey(cap);
      if (!affordable({ kind: "resource_cap_use", carrier: h.source, key, max: cap.max }, ctx.state, undefined)) continue;
      out.consumedCaps.push({ carrier: h.source, key });
    }
    const queuedBefore = out.forcedAttacks.length;
    const r = interpretEffect(h.effect.effect, { ...automaticEffectContext(ctx, h, overlay), forcedAttacks: out.forcedAttacks });
    // Its own `reaction` link (`chained-resolution-sequence` 4.3), naming the clause. A clause that
    // queued a forced attack (Aggression) is named by that attack's own link instead, so the
    // table reads its feature once, on the swing.
    out.steps.push(...(out.forcedAttacks.length > queuedBefore ? r.steps : asReactionLink(r.steps, { actor: h.source, feature: (h.effect.name ?? h.entityId ?? h.name)!, resume: ctx.attacker.id })));
    out.warnings.push(...r.warnings);
  }
  return out;
}

/**
 * The {@link TriggerFamily}s consumed by a registry-style interception window OUTSIDE the
 * production trigger bus — no `fireTrigger(` call site, so `resolve.ts`'s {@link
 * import("./resolve").FIRED_TRIGGER_POINTS} doesn't (and structurally can't, without conflating
 * two different firing mechanisms) name them.
 *
 * **What it's for:** `lvtt overlay coverage`'s INERT bucket (`inertTriggers.ts`) reports a clause
 * "loads, validates, never runs" from the fired-trigger list alone. Without this second list, every
 * reaction-registry entry — Leadership Die's reduce offer, Argonaut Shield's automatic split, and
 * Bondmate/Intercede's redirect — is false-flagged INERT even though `gatherReaction` runs each of
 * them on every qualifying hit. `inertTriggeredClauses` is parameterized on this list alongside
 * `firedTriggerPoints`, so the CLI unions both without `inertTriggers.ts` importing this module
 * directly (that import would be circular — THIS module imports the `effects` barrel that
 * `inertTriggers.ts` is re-exported through).
 *
 * `"damage"` — every `gatherReaction` sub-compiler keys on `h.effect.on === "damage"`. Also in
 * `FIRED_TRIGGER_POINTS` since `decisions/0175`: non-attack damage fires the non-optional clauses
 * on the bus (`resolveDamageTrigger`), the same two-mechanism shape as `"attack"` below.
 *
 * `"attack"` — the attack outcome's replacements and reactions ({@link import("./toHitReactionRegistry").outcomeReplacements},
 * {@link import("./toHitReactionRegistry").outcomeReactions}) consume `on: "attack"`, `optional: true` clauses (Turret Attack) OUTSIDE `fireTrigger`;
 * the production attack-family fire sites (`resolve/attack.ts`'s `fireAttackDeclared`,
 * `resolve/attackTriggerFires.ts`'s on_hit/on_miss) deliberately EXCLUDE `optional: true` clauses
 * (see their own comments), so an optional attack-family clause is reached ONLY through this
 * window, never the bus. Listed even though `"attack"` is ALSO in `FIRED_TRIGGER_POINTS` (for the
 * NON-optional clauses the bus fires) — the union makes it a no-op for today's INERT check, but the
 * honest record is that `"attack"` is consumed by TWO mechanisms, and a family that ISN'T already
 * bus-fired would need this exact entry to avoid a false INERT flag.
 *
 * **A new registry-consumed family joins this list in the SAME change that wires it**, mirroring
 * `FIRED_TRIGGER_POINTS`'s own discipline of growing only alongside the real wiring.
 */
export const REGISTRY_CONSUMED_TRIGGER_POINTS: readonly TriggerFamily[] = ["damage", "attack"];

/**
 * The fixed, deterministically-ordered chain of target-side reaction offers the resolver consults
 * at the interception window. Adding a target-side reaction is appending a {@link ReactionOffer}
 * here — the window already carries it. The array order IS the consult order (a fixed authored
 * list, not registration order); keep it stable for replay determinism.
 */
export const REACTION_OFFERS: readonly ReactionOffer[] = [BRACE_OFFER];

/** The built-in reaction-offer ids (the `<offerId>:<combatantId>` prompt-id prefix). */
const REACTION_OFFER_IDS: readonly string[] = REACTION_OFFERS.map((o) => o.id);

/**
 * Strip a barrage sub-attack namespace (`sub:<i>:<promptId>`) off a recorded-choice key, leaving
 * the bare prompt id (`brace:<combatantId>`). A non-namespaced key is returned untouched. Shared
 * by the cross-actor backend seam so it reads reaction answers the same way the resolver records
 * them, whether a single attack or a barrage sub-attack raised them.
 */
function deNamespace(key: string): string {
  return key.replace(/^sub:\d+:/, "");
}

/**
 * The shape an authored/protector-gathered offer's id takes: `<carrierId>#<clauseIndex>`
 * (`compileAuthoredOffers`/`compileProtectorOffers`) — as opposed to a fixed built-in id
 * (`"brace"`, no `#`). Recognition is by SHAPE, not a fixed list, because a fixed list can't
 * enumerate every dynamically-compiled authored offer — and a protector-gathered offer (Intercede)
 * needs the same `isReactionPromptId`/`reactionDefenderId` recognition Brace gets, since its
 * decider is a THIRD combatant (neither attacker nor target) that MUST be attributed correctly.
 */
const AUTHORED_OFFER_ID_SHAPE = /^.+#\d+$/;

/**
 * Build a reaction prompt id: `<offerId>:<deciderId>`, with `%` and `:` in the offer id
 * percent-escaped so {@link isReactionPromptId}/{@link reactionDefenderId}'s first-colon split
 * lands on the real separator even when the offer id embeds a colon-bearing combatant id
 * (`D:turret_drones:0#0`). Colon- and `%`-free offer ids (every built-in and every non-object
 * carrier) come out byte-identical to the old hand-built template, so recorded choices keep
 * their keys. Every reaction/to-hit-reaction offer MUST build its `promptId` through this.
 */
export function reactionPromptId(offerId: string, deciderId: string): string {
  return `${offerId.replace(/%/g, "%25").replace(/:/g, "%3A")}:${deciderId}`;
}

/**
 * Is `key` a recorded answer to a **reaction** prompt (`brace:<combatantId>`, or an authored/
 * protector-gathered `<carrierId>#<clauseIndex>:<deciderId>`), de-namespaced of any barrage
 * `sub:<i>:` prefix? The backend's cross-actor routing / attribution / auto-dismiss seam keys on
 * this to tell a defender's/protector's reaction answer apart from an ordinary same-actor `choose`
 * answer (variable damage, Lock On) without coupling to a fixed id list — the built-in ids are
 * checked verbatim; a dynamically-compiled authored/protector offer is recognized by its stable
 * `<offerId>:<deciderId>` SHAPE instead.
 *
 * **Colon-bearing ids.** Combatant ids CAN carry a colon — `deploy_object` (`spawnObjectGroup`)
 * and wreck conversion (`wreckIdFor`) mint `` `${deployerId}:${groupId}:${i}` `` / `` `${deadId}:wreck` `` —
 * and an authored offer id embeds its carrier's id (`` `${h.source}#${clauseIndex}` ``), so a
 * deployed object or wreck carrying a reaction clause yields an offer id with a colon in it. Every
 * prompt id is therefore built through {@link reactionPromptId}, which escapes the offer id so the
 * FIRST colon below is always the real `<offerId>:<deciderId>` separator. The decider suffix needs
 * no escaping: it is everything after that colon.
 */
export function isReactionPromptId(key: string): boolean {
  const bare = deNamespace(key);
  const colon = bare.indexOf(":");
  if (colon === -1) return false;
  const prefix = bare.slice(0, colon);
  return REACTION_OFFER_IDS.includes(prefix) || AUTHORED_OFFER_ID_SHAPE.test(prefix);
}

/**
 * The **decider** combatant id a reaction-prompt key names (`brace:<combatantId>` →
 * `<combatantId>`), de-namespaced of any barrage `sub:<i>:` prefix. Returns `undefined` for a
 * non-reaction key. The name comes from Brace's degenerate case (the decider IS the
 * defender/target), but a protector-gathered offer names a **protector** after the colon instead
 * (the third routing case: decider ≠ attacker ≠ target). Every prompt id's suffix is, by
 * construction (`<offerId>:<deciderId>`), the id of whoever must answer — this function just reads
 * it back generically.
 */
export function reactionDefenderId(key: string): string | undefined {
  if (!isReactionPromptId(key)) return undefined;
  const bare = deNamespace(key);
  return bare.slice(bare.indexOf(":") + 1);
}

/**
 * Consult the reaction registry at the interception window (after damage is rolled, before
 * `hp_delta`). **Composes EVERY eligible offer's {@link ReactionOutcome}** — not just the first,
 * or an authored offer would be unreachable on any mech target, since Brace is eligible on every
 * hit — for its recorded/defaulted answer, plus any automatic (non-optional) `on_damage_taken`
 * effect (Argonaut Shield's split) — or {@link NO_REACTION}-shaped when nothing fires. **Throws a
 * {@link NeedsDecisionSignal}** (carrying `who = target`) when an eligible offer is interactive
 * and unanswered — the resolver catches it and returns `needs_decision`; offers already resolved
 * earlier in the SAME call are simply recomputed identically on the next round-trip (pure over
 * `(ctx, choices)`).
 */
export function gatherReaction(ctx: ReactionContext): ReactionOutcome {
  // Reactions only fire during **sequenced combat** (a turn is in progress) — the same gate the
  // room's action-economy spend uses (`spend = acting && …`). RAW: a reaction responds to an
  // action taken on someone's turn, so in real combat a turn is always in progress when an attack
  // lands; this only suppresses the offer during un-sequenced setup (an idle encounter / a
  // spine-level attack with no turn structure), where the round-level reaction ledger is inert.
  if (ctx.state.activation.turn.phase !== "acting") return NO_REACTION;

  // Automatic (non-optional) on_damage_taken effects (Argonaut Shield's split): no prompt, no
  // decline, applied unconditionally when eligible. Gathered/applied FIRST so its `split` composes
  // into the outcome the same way an offer's does.
  const automatic = compileAutomaticOutcome(ctx);

  // **Multi-offer composition**: every ELIGIBLE offer in the fixed, deterministic list — built-ins
  // FIRST, target-class authored content NEXT, protector-gathered content LAST — is consulted and
  // its outcome composed, not just the first one.
  // RAW licenses this: Brace's Resistance, an authored damage-reduce/split, and a protector's
  // redirect are independent mechanisms (a reaction vs. a non-reaction resource spend vs. an
  // automatic consequence vs. a DIFFERENT combatant's own reaction) that can all apply to the SAME
  // hit. Each offer's own `promptId` keys its own recorded/defaulted answer, so a retained log
  // replays every offer's choice stably. An eligible-but-unanswered interactive offer still halts
  // the WHOLE gather with a `NeedsDecisionSignal` (one prompt at a time, the existing round-trip
  // contract) — offers already resolved earlier in this same call are simply recomputed
  // identically on the next round-trip (the gather is pure over `(ctx, choices)`, so re-running it
  // is cheap and deterministic).
  const offers: readonly ReactionOffer[] = ctx.offersClosed
    ? []
    : [...REACTION_OFFERS, ...compileAuthoredOffers(ctx), ...compileProtectorOffers(ctx)];
  return composeOffers(ctx, offers, automatic);
}

/**
 * **The Reliable-floor miss's damage window** (Core p.310's Rebound: "Trigger: The Ronin takes
 * damage from a ranged attack"). Reliable X "always does X damage, even if it misses", so a miss
 * that lands its floor is damage from the attack, and the target's authored damage-window offers
 * ({@link compileAuthoredOffers}) open on it exactly as on a hit. Brace does not (RAW "You are hit
 * by an attack"), nor do protector offers or Argonaut Shield's automatic split: those stay the hit
 * arm's, as they were before this window existed. The automatic `on:"damage"` clauses run once,
 * here, as {@link gatherDamageNotification} ran them; the settled `match.dealt` pass (Aggression)
 * is still the caller's separate {@link gatherSettledDamageOutcome} call, so nothing fires twice.
 *
 * Same envelope and exits as {@link gatherReaction}: sequenced combat only, `offersClosed` shuts
 * the offers (a forced reflection), and an unanswered interactive offer throws a
 * {@link NeedsDecisionSignal}.
 */
export function gatherReliableMissReaction(ctx: ReactionContext): ReactionOutcome {
  if (ctx.state.activation.turn.phase !== "acting") return NO_REACTION;
  const automatic = compileAutomaticOutcome(ctx);
  return composeOffers(ctx, ctx.offersClosed ? [] : compileAuthoredOffers(ctx), { steps: automatic.steps, warnings: automatic.warnings, spendReactionBy: automatic.spendReactionBy, named: automatic.named });
}

/**
 * Consult every offer in order for its recorded, prompted or defaulted answer and compose the
 * outcomes onto the automatic clauses' own — {@link gatherReaction}'s multi-offer composition,
 * shared with {@link gatherReliableMissReaction}.
 */
function composeOffers(ctx: ReactionContext, offers: readonly ReactionOffer[], automatic: ReturnType<typeof compileAutomaticOutcome>): ReactionOutcome {
  let resistant = false;
  let reduce = 0;
  let redirect: { partnerId: string } | undefined;
  const spendReactionBy: { combatantId: string; name: string }[] = [...automatic.spendReactionBy];
  const steps: ResolvedStep[] = [...automatic.steps];
  const warnings: string[] = [...automatic.warnings];
  const forcedAttacks: ForcedAttackRequest[] = [];
  for (const offer of offers) {
    if (!offer.eligible(ctx)) continue;
    const promptId = offer.promptId(ctx);
    const recorded = ctx.choices?.[promptId];
    const optionIds = offer.options.map((o) => o.id);
    let outcome: ReactionOutcome;
    if (recorded !== undefined && optionIds.includes(recorded)) {
      outcome = offer.outcome(ctx, recorded);
    } else if (ctx.interactive) {
      // `who` is per-offer: the target for Brace/a target-class authored offer (the degenerate
      // case), the PROTECTOR for a protector-gathered offer — neither the attacker nor the target.
      throw new NeedsDecisionSignal(promptId, offer.prompt(ctx), {
        options: offer.options,
        who: offer.who(ctx),
        title: offer.title,
        ...(offer.summary !== undefined ? { summary: offer.summary } : {}),
      });
    } else {
      // Non-interactive (replay / CLI) with no recorded answer: the deterministic default — decline.
      outcome = offer.outcome(ctx, DECLINE_OPTION);
    }
    resistant = resistant || outcome.resistant;
    reduce += outcome.reduce ?? 0;
    steps.push(...outcome.steps);
    if (outcome.spendReactionBy) spendReactionBy.push(...outcome.spendReactionBy);
    if (outcome.redirect) redirect = outcome.redirect;
    if (outcome.warnings) warnings.push(...outcome.warnings);
    if (outcome.forcedAttacks) forcedAttacks.push(...outcome.forcedAttacks);
  }
  return {
    resistant,
    steps,
    ...(spendReactionBy.length > 0 ? { spendReactionBy } : {}),
    ...(reduce > 0 ? { reduce } : {}),
    ...(automatic.split ? { split: automatic.split } : {}),
    ...(automatic.named.length > 0 ? { named: automatic.named } : {}),
    ...(redirect ? { redirect } : {}),
    ...(warnings.length > 0 ? { warnings } : {}),
    ...(forcedAttacks.length > 0 ? { forcedAttacks } : {}),
  };
}
